import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { AttachFile, Close } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

interface Props {
  files: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
  maxSizeMb?: number;
}

const ALLOWED_TYPES = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf', 'text/plain', 'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export const FeedbackAttachments = ({ files, onChange, maxFiles = 10, maxSizeMb = 10 }: Props) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const maxBytes = maxSizeMb * 1048576;

  const addFiles = useCallback((newFiles: File[]) => {
    const valid = newFiles.filter((f) => f.size <= maxBytes);
    // For images, allow any image type; for others, check ALLOWED_TYPES
    const filtered = valid.filter(
      (f) => f.type.startsWith('image/') || ALLOWED_TYPES.includes(f.type),
    );
    onChange([...files, ...filtered].slice(0, maxFiles));
  }, [files, onChange, maxFiles, maxBytes]);

  const removeFile = useCallback((idx: number) => {
    onChange(files.filter((_, i) => i !== idx));
  }, [files, onChange]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files));
    if (inputRef.current) inputRef.current.value = '';
  }, [addFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  // Generate object URLs for image thumbnails
  const previews = useMemo(() =>
    files.map((f) => f.type.startsWith('image/') ? URL.createObjectURL(f) : null),
  [files]);

  // Clean up object URLs
  useEffect(() => {
    return () => previews.forEach((url) => url && URL.revokeObjectURL(url));
  }, [previews]);

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      sx={{
        border: files.length ? 'none' : '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        p: files.length ? 0 : 1.5,
        minHeight: files.length ? 'auto' : 40,
      }}
    >
      {files.length === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" startIcon={<AttachFile />}
            onClick={() => inputRef.current?.click()}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
            Attach files
          </Button>
          <Typography variant="caption" color="text.secondary">
            or paste screenshots (Ctrl+V) / drag & drop
          </Typography>
        </Box>
      )}

      {files.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {files.map((f, i) => (
            <Box key={`${f.name}-${i}`} sx={{
              position: 'relative',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: alpha(theme.palette.action.hover, 0.3),
            }}>
              {previews[i] ? (
                <Box component="img" src={previews[i]!}
                  sx={{ display: 'block', maxWidth: 180, maxHeight: 120, objectFit: 'contain' }} />
              ) : (
                <Box sx={{ px: 2, py: 1.5, maxWidth: 180 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', wordBreak: 'break-all' }}>
                    {f.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                    {formatSize(f.size)}
                  </Typography>
                </Box>
              )}
              <IconButton size="small" onClick={() => removeFile(i)}
                sx={{
                  position: 'absolute', top: 2, right: 2,
                  bgcolor: 'rgba(0,0,0,0.5)', color: '#fff',
                  width: 20, height: 20,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}>
                <Close sx={{ fontSize: '0.75rem' }} />
              </IconButton>
            </Box>
          ))}

          {files.length < maxFiles && (
            <Button size="small" onClick={() => inputRef.current?.click()}
              sx={{
                textTransform: 'none', fontSize: '0.72rem', minWidth: 0,
                border: `1px dashed ${theme.palette.divider}`, borderRadius: 1,
                px: 2, alignSelf: 'center',
              }}>
              + Add
            </Button>
          )}
        </Box>
      )}

      <input ref={inputRef} type="file" multiple
        accept="image/*,.pdf,.txt,.csv,.xlsx"
        onChange={handleFileInput} style={{ display: 'none' }} />
    </Box>
  );
};
